// ---------------------------------------------------------------------------
// MultiMonthUploadModal — envia varios arquivos do mesmo input pra
// meses diferentes, em um fluxo guiado de 3 passos.
// ---------------------------------------------------------------------------
// Acionado pelo botao "Enviar multiplos meses" no toolbar do
// ``InputDrawerV3``, visivel apenas quando ``volatility === "monthly"``
// (rev. 2026-05-28: ``semi_fixed`` foi consolidado em ``monthly`` +
// flag ``reuse_auto``, migration 0035; o botao continua aparecendo
// para ``monthly+reuse_auto`` porque cada slot mensal e' independente).
//
// Construido sobre as PRIMITIVAS centralizadas:
//   - ``Dialog`` + ``Transition`` (mesma anim. de SpecialOptionsModal /
//     ProgressDashboardModal / ResetInputsDialog).
//   - ``ModalHeader`` (header dark, strip do cliente, pill, botao X).
//   - ``DialogContent`` + ``DialogActions`` + ``CustomButtons/Button``
//     (footer com botoes consistentes com o resto da app).
//
// Fluxo (cf. ``useMultiMonthUpload``):
//   Step 1 — Arquivos e periodo
//   Step 2 — Confirmacao (pareamento sugerido entre arquivos e meses)
//   Step 3 — Upload (sequencial, com contadores e status por linha)
//
// Detalhes de UX (alinhados com o feedback do consultor):
//   - Numeros do stepper usam ``clientColor`` (atual/done) e
//     ``clientColor`` diluido (pendente).
//   - Botoes "Selecionar pasta/arquivos" usam a classe local
//     ``filePickerBtn`` (ghost button compacto, mesmo visual do
//     "Enviar multiplos meses" do drawer, so que com font/padding
//     menores pra caber bem na linha do step 1).
//   - Footer com "Voltar" a esquerda + "Cancelar/Avancar" a direita.
//   - Step 2 detecta meses duplicados: marca a row, mostra um badge
//     "Duplicado" e desabilita "Iniciar upload".
//   - Pill de confianca centralizada no header e em cada row.
//   - Titulo do header usa o ``readableName`` (mesma helper do
//     ``DrawerHeader``).
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";

import CloudUpload from "@material-ui/icons/CloudUpload";
import CheckCircle from "@material-ui/icons/CheckCircle";
import ErrorOutline from "@material-ui/icons/ErrorOutline";

import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

import FolderOpen from "@material-ui/icons/FolderOpen";
import InsertDriveFile from "@material-ui/icons/InsertDriveFile";
import Refresh from "@material-ui/icons/Refresh";
import WarningIcon from "@material-ui/icons/Warning";
// Icones de status do Step 3 — escolhidos pra terem o MESMO peso
// visual: glifos simples (sem circulo embutido), renderizados dentro
// do circulo de fundo colorido (``classes.uploadIcon``). Familia
// uniforme = leitura limpa em sequencia.
import Schedule from "@material-ui/icons/Schedule"; // aguardando
import ArrowUpward from "@material-ui/icons/ArrowUpward"; // enviando
import Check from "@material-ui/icons/Check"; // sucesso
import CloseIcon from "@material-ui/icons/Close"; // falha
import Remove from "@material-ui/icons/Remove"; // interrompido

import Button from "../../../../components/CustomButtons/Button";
import ModalHeader from "../../../../components/ModalHeader";
import useThemeColors from "../../../../hooks/useThemeColors";
import { Transition } from "../../inputs_components";
import { getAcceptExtensions } from "../../inputs_components/utils/fileTypes";

import useMultiMonthStyles from "./styles";
import MonthRangeGrid, { formatMonthShort } from "./MonthRangeGrid";
import useMultiMonthUpload, { MODES, STEPS } from "./useMultiMonthUpload";

const MONTH_ABBR_PT = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

// Mesma helper usada pelo ``DrawerHeader`` — escolhemos o nome legivel
// do input considerando i18n quando disponivel.
function readableInputName(input, lang) {
  if (!input) return "Input";
  if (input.input_name && typeof input.input_name === "object") {
    return (
      input.input_name[lang] ||
      input.input_name.pt ||
      input.meta_name ||
      "Input"
    );
  }
  return input.display_name || input.meta_name || "Input";
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function summarizeKeys(keys) {
  if (!keys || keys.size === 0) {
    return { text: "Nenhum mês selecionado", count: 0 };
  }
  const sorted = [...keys].sort((a, b) => {
    const [ay, am] = a.split("-").map(Number);
    const [by, bm] = b.split("-").map(Number);
    return ay * 12 + am - (by * 12 + bm);
  });
  if (sorted.length === 1) {
    return { text: formatMonthShort(sorted[0]), count: 1 };
  }
  return {
    text: `${formatMonthShort(sorted[0])} – ${formatMonthShort(
      sorted[sorted.length - 1],
    )}`,
    count: sorted.length,
  };
}

// Mapeia o ``step`` interno do hook para a posicao visual no stepper.
// O modo SINGLE renumera as etapas: 1=Arquivo+Periodo, 2=Upload.
// Como o hook nunca emite ``MATCH``/``MATCH_LOADING`` em SINGLE, a
// branch nao colide; mas defendemos via ``mode`` para o caso de o
// usuario alternar de modo no meio do fluxo (que ja vai resetar o
// step para FILES, mas a leitura defensiva torna a funcao pura).
function stepIndex(step, mode) {
  if (step === STEPS.FILES) return 1;
  if (mode === MODES.SINGLE) {
    return 2; // STEPS.UPLOAD / UPLOAD_DONE -> step 2 no modo single
  }
  if (step === STEPS.MATCH || step === STEPS.MATCH_LOADING) return 2;
  return 3;
}

function MultiMonthUploadModal({
  open,
  onClose,
  appName,
  input,
  availableMonths,
  anchorYear,
  anchorMonth,
  onUploaded,
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language || "pt").slice(0, 2);
  const inputDisplayName = readableInputName(input, lang);
  const metaName = input?.meta_name || "";

  const { clientColor } = useThemeColors();
  const classes = useMultiMonthStyles({ clientColor });

  const api = useMultiMonthUpload({
    open,
    appName,
    metaName,
    inputDisplayName,
    expectedFileTypes: input?.expected_file_types ?? input?.expected_file_type,
    availableMonths,
    anchorYear,
    anchorMonth,
    onUploaded,
  });

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const isSingleMode = api.mode === MODES.SINGLE;

  // ESC + handlers de fechar: bloqueado durante upload em curso.
  const blockClose = api.isUploading;

  // ── Step indexes / helpers ──────────────────────────────────────────
  const stepIdx = stepIndex(api.step, api.mode);
  const spinnerOn = api.isMatching || api.isUploading;
  // Numero total de etapas exibidas no stepper. SINGLE = 2, MULTI = 3.
  const totalSteps = isSingleMode ? 2 : 3;
  // No modo SINGLE o titulo do step UPLOAD tem outro indice (2 em vez
  // de 3). Centralizamos aqui para nao espalhar literais.
  const uploadStepNumber = totalSteps;

  const stepClass = (n) =>
    cx(classes.step, {
      [classes.stepDone]: n < stepIdx,
      [classes.stepCurrent]: n === stepIdx,
      [classes.stepPending]: n > stepIdx,
    });

  // ── File pickers ────────────────────────────────────────────────────
  const handlePickFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };
  const handlePickFolder = () => {
    if (folderInputRef.current) {
      folderInputRef.current.value = "";
      folderInputRef.current.click();
    }
  };
  const handleFilesChosen = (e) => {
    api.addFiles(e.target.files);
    e.target.value = "";
  };

  // ── Resumo de meses + overlap (Step 1) ──────────────────────────────
  const monthSummary = useMemo(
    () => summarizeKeys(api.selectedKeys),
    [api.selectedKeys],
  );
  const overlapKeys = useMemo(() => {
    const out = [];
    api.selectedKeys.forEach((k) => {
      if (api.deliveredKeys.has(k)) out.push(k);
    });
    return out.sort((a, b) => {
      const [ay, am] = a.split("-").map(Number);
      const [by, bm] = b.split("-").map(Number);
      return ay * 12 + am - (by * 12 + bm);
    });
  }, [api.selectedKeys, api.deliveredKeys]);

  // ── Step 2: duplicidade de meses no pareamento ──────────────────────
  // Um mes nao pode ser destino de mais de um arquivo. Identificamos as
  // linhas duplicadas (qualquer mes que aparece >= 2 vezes nas pairs).
  // ``Iniciar upload`` fica disabled enquanto houver dupes.
  const { dupeKeys, dupeCount } = useMemo(() => {
    const counts = new Map();
    api.pairs.forEach((p) => {
      if (!p.month) return;
      counts.set(p.month, (counts.get(p.month) || 0) + 1);
    });
    const dupes = new Set();
    counts.forEach((c, k) => {
      if (c > 1) dupes.add(k);
    });
    return { dupeKeys: dupes, dupeCount: dupes.size };
  }, [api.pairs]);

  // ── ESC global (so quando aberto + nao bloqueado) ───────────────────
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape" && !blockClose) onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, blockClose, onClose]);

  // ── Render ──────────────────────────────────────────────────────────
  // ``keepMounted`` ajuda em re-aberturas (mantem estado do hook).
  // Sem early return: todos os hooks acima rodam SEMPRE. O ``Dialog``
  // ja resolve a renderizacao condicional via ``open``.
  return (
    <Dialog
      open={open}
      onClose={blockClose ? undefined : onClose}
      TransitionComponent={Transition}
      keepMounted
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.paper }}
      aria-labelledby="multi-month-upload-title"
    >
      <ModalHeader
        id="multi-month-upload-title"
        title={inputDisplayName}
        subtitle={t(
          isSingleMode
            ? "modeling_inputs_v3.multi_month_upload.mode_single_title"
            : "modeling_inputs_v3.multi_month_upload.mode_multi_title",
        )}
        rightContent={
          <>
            {/* Spinner primeiro (mais a esquerda) para deixar a pill */}
            {/* colada no botao de fechar (X), facilitando achar a */}
            {/* alternancia visualmente. */}
            <span
              className={cx(classes.headerSpinnerSlot, {
                [classes.headerSpinnerOn]: spinnerOn,
              })}
              aria-hidden="true"
            >
              <ThreeDots
                color="#ffffff"
                height="12"
                width="38"
                wrapperStyle={{ height: "12px", width: "38px" }}
              />
            </span>
            {/* Pill segmentada para alternar entre os dois modos. */}
            {/* Bloqueada enquanto match/upload estiverem em curso para */}
            {/* nao trocar de fluxo no meio da operacao. */}
            <div
              className={classes.modePill}
              role="tablist"
              aria-label={t(
                "modeling_inputs_v3.multi_month_upload.mode_multi_title",
              )}
            >
              <button
                type="button"
                role="tab"
                aria-selected={!isSingleMode}
                disabled={spinnerOn}
                onClick={() => api.setMode(MODES.MULTI)}
                className={cx(classes.modePillBtn, {
                  [classes.modePillBtnActive]: !isSingleMode,
                })}
              >
                {t("modeling_inputs_v3.multi_month_upload.mode_multi_label")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={isSingleMode}
                disabled={spinnerOn}
                onClick={() => api.setMode(MODES.SINGLE)}
                className={cx(classes.modePillBtn, {
                  [classes.modePillBtnActive]: isSingleMode,
                })}
              >
                {t("modeling_inputs_v3.multi_month_upload.mode_single_label")}
              </button>
            </div>
          </>
        }
        onClose={onClose}
        closeDisabled={blockClose}
        closeLabel="Fechar"
        clientColor={clientColor}
      />

      {/* Stepper — 3 passos no modo MULTI, 2 no modo SINGLE. */}
      {/* No SINGLE, o passo intermediario de "Confirmacao" (pareamento) */}
      {/* deixa de existir porque so ha 1 arquivo: nada para parear. */}
      <div className={classes.stepper}>
        <div className={stepClass(1)}>
          <span className={classes.stepNum}>{stepIdx > 1 ? "✓" : "1"}</span>
          <span className={classes.stepLbl}>
            <span className={classes.stepLblT}>
              {isSingleMode ? "Arquivo e período" : "Arquivos e período"}
            </span>
            <span className={classes.stepLblH}>
              {isSingleMode
                ? "Selecione o arquivo e meses"
                : "Selecione arquivos e meses"}
            </span>
          </span>
        </div>
        {!isSingleMode && (
          <div className={stepClass(2)}>
            <span className={classes.stepNum}>{stepIdx > 2 ? "✓" : "2"}</span>
            <span className={classes.stepLbl}>
              <span className={classes.stepLblT}>Confirmação</span>
              <span className={classes.stepLblH}>Revise o pareamento</span>
            </span>
          </div>
        )}
        <div className={stepClass(uploadStepNumber)}>
          <span className={classes.stepNum}>{uploadStepNumber}</span>
          <span className={classes.stepLbl}>
            <span className={classes.stepLblT}>Upload</span>
            <span className={classes.stepLblH}>Acompanhe o envio</span>
          </span>
        </div>
      </div>

      <DialogContent className={classes.body}>
        <div className={cx(classes.stepBodyWrap, classes.scroll)}>
          {stepIdx === 1 && (
            <Step1Body
              classes={classes}
              api={api}
              t={t}
              isSingleMode={isSingleMode}
              expectedFileTypes={
                input?.expected_file_types ?? input?.expected_file_type
              }
              fileInputRef={fileInputRef}
              folderInputRef={folderInputRef}
              onPickFiles={handlePickFiles}
              onPickFolder={handlePickFolder}
              onFilesChosen={handleFilesChosen}
              monthSummary={monthSummary}
              overlapKeys={overlapKeys}
            />
          )}
          {/* No MULTI, stepIdx===2 e o pareamento; no SINGLE, e o upload. */}
          {stepIdx === 2 && !isSingleMode && (
            <Step2Body
              classes={classes}
              api={api}
              dupeKeys={dupeKeys}
              dupeCount={dupeCount}
            />
          )}
          {stepIdx === 2 && isSingleMode && (
            <SingleUploadBody classes={classes} api={api} t={t} />
          )}
          {stepIdx === 3 && <Step3Body classes={classes} api={api} />}
        </div>
      </DialogContent>

      <DialogActions className={classes.footer}>
        {/* Botao Voltar so faz sentido no MULTI step 2 (pareamento). */}
        {/* No SINGLE step 2 (upload) ou no step 3 do MULTI, voltar nao */}
        {/* e' opcao porque a operacao ja esta em curso ou ja foi. */}
        {!isSingleMode && stepIdx === 2 && (
          <div className="footerLeft">
            <Button
              onClick={api.goBack}
              color="black"
              simple
              disabled={api.isMatching}
              size="sm"
            >
              Voltar
            </Button>
          </div>
        )}
        {/* Cancelar fica visivel ate o usuario disparar o upload. */}
        {/* Step 1 (ambos modos) + step 2 (MULTI/pareamento). */}
        {(stepIdx === 1 || (stepIdx === 2 && !isSingleMode)) && (
          <Button
            onClick={() => !blockClose && onClose()}
            color="black"
            simple
            size="sm"
          >
            Cancelar
          </Button>
        )}
        {/* Step 1 -> proxima etapa. MULTI dispara o match (passo 2 */}
        {/* de pareamento); SINGLE dispara o broadcast direto. */}
        {stepIdx === 1 && (
          <Button
            onClick={isSingleMode ? api.startBroadcastUpload : api.startMatch}
            color="client"
            size="sm"
            disabled={!api.canProceedFromFiles}
          >
            {isSingleMode ? "Iniciar upload" : "Prosseguir"}
          </Button>
        )}
        {/* MULTI step 2 (pareamento) -> Iniciar upload. */}
        {stepIdx === 2 && !isSingleMode && (
          <Button
            onClick={api.startUpload}
            color="client"
            size="sm"
            disabled={api.isMatching || !api.canStartUpload || dupeCount > 0}
          >
            Iniciar upload
          </Button>
        )}
        {/* Step UPLOAD (3 no MULTI, 2 no SINGLE) — Fechar/Interromper. */}
        {stepIdx === uploadStepNumber && !api.isUploading && (
          <Button onClick={onClose} color="client" size="sm">
            Fechar
          </Button>
        )}
        {stepIdx === uploadStepNumber && api.isUploading && !isSingleMode && (
          <Button onClick={api.interruptUpload} color="black" simple size="sm">
            Interromper
          </Button>
        )}
        {/* SINGLE: nao oferecemos "Interromper" durante o upload porque */}
        {/* ha apenas uma chamada HTTP em curso e cancela-la deixa o */}
        {/* backend com periodos parciais aplicados (a transaction so */}
        {/* aborta se o erro acontece DURANTE a request, nao do lado */}
        {/* do cliente apos o body ter sido recebido). Footer fica */}
        {/* vazio do lado direito enquanto sobe. */}
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Step 1
// ─────────────────────────────────────────────────────────────────────

function Step1Body({
  classes,
  api,
  t,
  isSingleMode,
  expectedFileTypes,
  fileInputRef,
  folderInputRef,
  onPickFiles,
  onPickFolder,
  onFilesChosen,
  monthSummary,
  overlapKeys,
}) {
  const accept = getAcceptExtensions(expectedFileTypes);
  return (
    <>
      <div className={classes.sectionTitle}>
        {isSingleMode
          ? t(
              "modeling_inputs_v3.multi_month_upload.single_step1_section_title",
            )
          : "Arquivos"}
      </div>
      <div className={classes.filePickers}>
        {/* Folder picker so existe no MULTI — em SINGLE selecionar uma */}
        {/* pasta inteira nao faz sentido (so 1 arquivo sera aceito). */}
        {!isSingleMode && (
          <input
            ref={folderInputRef}
            type="file"
            /* eslint-disable-next-line react/no-unknown-property */
            webkitdirectory=""
            /* eslint-disable-next-line react/no-unknown-property */
            directory=""
            multiple
            style={{ display: "none" }}
            onChange={onFilesChosen}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          /* MULTI permite selecao de varios; SINGLE aceita um so. */
          multiple={!isSingleMode}
          accept={accept}
          style={{ display: "none" }}
          onChange={onFilesChosen}
        />
        {!isSingleMode && (
          <button
            type="button"
            className={classes.filePickerBtn}
            onClick={onPickFolder}
          >
            <FolderOpen />
            Selecionar pasta
          </button>
        )}
        <button
          type="button"
          className={classes.filePickerBtn}
          onClick={onPickFiles}
        >
          <InsertDriveFile />
          {isSingleMode
            ? t("modeling_inputs_v3.multi_month_upload.single_pick_file")
            : "Selecionar arquivos"}
        </button>
        <span className={classes.filePickerSpacer} />
        {/* SINGLE: nao oferecemos atalho "trocar arquivo" porque ele */}
        {/* e' redundante — o usuario ja pode clicar de novo em */}
        {/* "Selecionar arquivo" (substitui) ou no ``×`` do chip. */}
        {!isSingleMode && api.files.length > 0 && (
          <button
            type="button"
            className={classes.fileLinkBtn}
            onClick={api.clearFiles}
          >
            Limpar lista
          </button>
        )}
      </div>
      {/* Apresentacao do(s) arquivo(s) selecionado(s). */}
      {/* SINGLE: chip compacto (1 linha) — box scrollavel + contador */}
      {/* "X arquivos selecionados" nao fazem sentido para 1 arquivo. */}
      {/* MULTI: lista scrollavel + meta line com totalizadores. */}
      {isSingleMode ? (
        <div className={classes.singleFileLine}>
          {api.files.length === 0 ? (
            <span className={classes.singleFileLineEmpty}>
              {t("modeling_inputs_v3.multi_month_upload.single_file_empty")}
            </span>
          ) : (
            <span className={classes.singleFileChip} title={api.files[0].name}>
              <InsertDriveFile style={{ fontSize: 14, opacity: 0.7 }} />
              <span className={classes.singleFileChipName}>
                {api.files[0].name}
              </span>
              <span className={classes.singleFileChipSize}>
                {formatFileSize(api.files[0].size)}
              </span>
              <button
                type="button"
                className={classes.singleFileChipRemove}
                onClick={() => api.removeFile(0)}
                aria-label="Remover"
              >
                ×
              </button>
            </span>
          )}
        </div>
      ) : (
        <>
          <div className={cx(classes.fileList, classes.scroll)}>
            {api.files.length === 0 ? (
              <div className={classes.fileEmpty}>
                Nenhum arquivo selecionado ainda.
              </div>
            ) : (
              api.files.map((file, idx) => (
                <div className={classes.fileRow} key={`${file.name}-${idx}`}>
                  <InsertDriveFile style={{ fontSize: 14, opacity: 0.6 }} />
                  <span className={classes.fileName} title={file.name}>
                    {file.name}
                  </span>
                  <span className={classes.fileSize}>
                    {formatFileSize(file.size)}
                  </span>
                  <button
                    type="button"
                    className={classes.fileRemove}
                    onClick={() => api.removeFile(idx)}
                    aria-label="Remover"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
          <div className={classes.fileMeta}>
            <span>
              {api.files.length === 0
                ? "0 arquivos selecionados"
                : api.files.length === 1
                  ? "1 arquivo selecionado"
                  : `${api.files.length} arquivos selecionados`}
              {api.files.length >= api.maxFiles && (
                <span style={{ marginLeft: 8, opacity: 0.7 }}>
                  (limite: {api.maxFiles})
                </span>
              )}
            </span>
            <span>
              {api.files.length > 0 &&
                `Total: ${formatFileSize(
                  api.files.reduce((acc, f) => acc + f.size, 0),
                )}`}
            </span>
          </div>
        </>
      )}

      <div className={classes.monthGridWrap}>
        <div className={classes.sectionTitle}>Período</div>
        <MonthRangeGrid
          availableMonths={api.availableMonths}
          anchorYear={api.anchorYear}
          selectedKeys={api.selectedKeys}
          deliveredKeys={api.deliveredKeys}
          onChange={api.setSelectedKeys}
          classes={classes}
        />
        <div className={classes.monthSummary}>
          <span style={{ color: "#aaa" }}>Período selecionado:</span>
          <strong>{monthSummary.text}</strong>
          <span className={classes.monthSummaryRight}>
            {monthSummary.count > 0 &&
              (monthSummary.count === 1
                ? "1 mês"
                : `${monthSummary.count} meses`)}
          </span>
        </div>
        <div
          className={cx(classes.overlapWarn, {
            [classes.overlapWarnOff]: overlapKeys.length === 0,
          })}
        >
          <WarningIcon style={{ fontSize: 16, color: "#b45c00" }} />
          <div>
            <strong>
              {overlapKeys.length === 1
                ? "1 mês já tem arquivo enviado"
                : `${overlapKeys.length} meses já têm arquivo enviado`}
            </strong>
            {overlapKeys.length > 0 && (
              <>
                {" "}
                ({overlapKeys.map(formatMonthShort).join(", ")}). Continuar irá{" "}
                <strong>substituir</strong>
                {overlapKeys.length === 1
                  ? " o arquivo atual"
                  : " os arquivos atuais"}
                .
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Step 2
// ─────────────────────────────────────────────────────────────────────

function Step2Body({ classes, api, dupeKeys, dupeCount }) {
  // Opcoes do select: meses selecionados no Step 1, em ordem cronologica.
  const monthOptions = useMemo(() => {
    const list = [...api.selectedKeys].sort((a, b) => {
      const [ay, am] = a.split("-").map(Number);
      const [by, bm] = b.split("-").map(Number);
      return ay * 12 + am - (by * 12 + bm);
    });
    return list.map((k) => {
      const [y, m] = k.split("-").map(Number);
      return { value: k, label: `${MONTH_ABBR_PT[m - 1]}/${y}` };
    });
  }, [api.selectedKeys]);

  // Conjunto de meses ja em uso por algum arquivo. Renderizamos as
  // opcoes equivalentes em cinza dentro de cada select (exceto a
  // propria opcao da row, que continua "normal" pra nao mover o
  // valor selecionado). "Ignorar" nunca entra nessa conta.
  const usedMonths = useMemo(() => {
    const s = new Set();
    api.pairs.forEach((p) => {
      if (p.month) s.add(p.month);
    });
    return s;
  }, [api.pairs]);

  return (
    <>
      <div className={classes.sectionTitle}>Pareamento sugerido</div>
      <div
        className={cx(classes.matchArea, {
          [classes.matchAreaLoading]: api.isMatching,
        })}
      >
        <div className={classes.matchHead}>
          <div>Arquivo</div>
          <div>Mês</div>
          <div className={classes.matchHeadConf}>Confiança</div>
        </div>
        <div className={cx(classes.matchBody, classes.scroll)}>
          {api.pairs.map((p, idx) => {
            const isDupe = !!p.month && dupeKeys.has(p.month);
            return (
              <div
                key={`${p.file.name}-${idx}`}
                className={cx(classes.matchRow, {
                  [classes.matchRowUnmatched]: !p.month,
                  [classes.matchRowDupe]: isDupe,
                })}
              >
                <div className={classes.matchFileName} title={p.file.name}>
                  {p.file.name}
                </div>
                <div className={classes.matchSelectWrap}>
                  <select
                    className={cx(classes.matchSelect, {
                      [classes.matchSelectDupe]: isDupe,
                    })}
                    value={p.month || ""}
                    onChange={(e) =>
                      api.setPairMonth(idx, e.target.value || null)
                    }
                    disabled={api.isMatching}
                  >
                    <option value="">— Ignorar —</option>
                    {monthOptions.map((o) => {
                      // "Cinza" = mes ja escolhido por OUTRA linha. A
                      // propria selecao da row continua em preto.
                      const usedElsewhere =
                        usedMonths.has(o.value) && o.value !== p.month;
                      return (
                        <option
                          key={o.value}
                          value={o.value}
                          style={usedElsewhere ? { color: "#aaa" } : undefined}
                        >
                          {o.label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <ConfidencePill
                  classes={classes}
                  confidence={p.month ? p.confidence : "none"}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={cx(classes.matchSummary, {
          [classes.matchSummaryDupe]: dupeCount > 0,
        })}
      >
        <span>
          <strong>{api.pairCounts.matched}</strong> de{" "}
          <strong>{api.pairCounts.total}</strong> arquivos pareados
        </span>
        <span>
          {dupeCount > 0 ? (
            <>
              <strong>{dupeCount}</strong>{" "}
              {dupeCount === 1 ? "mês duplicado" : "meses duplicados"} — cada
              mês só aceita um arquivo
            </>
          ) : api.pairCounts.unmatched > 0 ? (
            <>
              <strong>{api.pairCounts.unmatched}</strong> sem mês (será
              ignorado)
            </>
          ) : null}
        </span>
      </div>
    </>
  );
}

function ConfidencePill({ classes, confidence }) {
  const LABEL = {
    high: "Alta",
    medium: "Média",
    manual: "Manual",
    none: "Sem mês",
  };
  const ICON = {
    high: "●",
    medium: "◐",
    manual: "✎",
    none: "✕",
  };
  const klass = {
    high: classes.confHigh,
    medium: classes.confMedium,
    manual: classes.confManual,
    none: classes.confNone,
  }[confidence];
  return (
    <span className={cx(classes.conf, klass)}>
      {ICON[confidence]} {LABEL[confidence]}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Step 3
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// SingleUploadBody — Step UPLOAD do modo "Unico arquivo"
// ─────────────────────────────────────────────────────────────────────
// Substitui a lista de N rows do Step 3 do modo MULTI por uma card
// central com barra de progresso, icone de status e texto. Reflete
// honestamente a estrutura da API: ha UMA so request HTTP, entao
// nao faz sentido fingir N uploads paralelos.
//
// Estados:
//   - uploading  -> icone de upload + barra animada + texto
//                   "Aplicando em N meses..."
//   - success    -> check verde + barra full + texto "concluido"
//   - error      -> X vermelho + texto de erro + barra vermelha
function SingleUploadBody({ classes, api, t }) {
  const monthCount = api.selectedKeys.size;
  const status = api.singleStatus;
  const pct = api.singleProgress;

  // Mapeamento icone/cor por status — mesma familia visual do modo
  // MULTI (success verde, error vermelho, neutro = cor do cliente).
  const Icon =
    status === "success"
      ? CheckCircle
      : status === "error"
        ? ErrorOutline
        : CloudUpload;
  const iconClass =
    status === "success"
      ? classes.singleIconSuccess
      : status === "error"
        ? classes.singleIconError
        : classes.singleIconUploading;
  const fillClass =
    status === "success"
      ? classes.singleProgressFillSuccess
      : status === "error"
        ? classes.singleProgressFillError
        : null;
  const fillWidth =
    status === "success" || status === "error" ? 100 : Math.min(99, pct);

  const titleText =
    status === "success"
      ? t("modeling_inputs_v3.multi_month_upload.single_step2_done", {
          count: monthCount,
        })
      : status === "error"
        ? t("modeling_inputs_v3.multi_month_upload.single_step2_failed")
        : t("modeling_inputs_v3.multi_month_upload.single_step2_progress", {
            count: monthCount,
          });

  return (
    <div className={classes.singleUploadCard}>
      <span className={cx(classes.singleUploadIcon, iconClass)}>
        <Icon style={{ fontSize: 28 }} />
      </span>
      <div className={classes.singleUploadText}>{titleText}</div>
      {status === "error" && api.singleError && (
        <div
          className={classes.singleUploadSub}
          title={String(api.singleError)}
        >
          {String(api.singleError)}
        </div>
      )}
      <div className={classes.singleProgressBar}>
        <div
          className={cx(classes.singleProgressFill, fillClass)}
          style={{ width: `${fillWidth}%` }}
        />
      </div>
    </div>
  );
}

function Step3Body({ classes, api }) {
  return (
    <>
      <div className={classes.uploadCounters}>
        <div className={cx(classes.stat, classes.statSuccess)}>
          <span className={classes.statV}>{api.uploadCounts.success}</span>
          <span className={classes.statL}>Enviados</span>
        </div>
        <div className={cx(classes.stat, classes.statError)}>
          <span className={classes.statV}>{api.uploadCounts.error}</span>
          <span className={classes.statL}>Falharam</span>
        </div>
        <div className={cx(classes.stat, classes.statWaiting)}>
          <span className={classes.statV}>{api.uploadCounts.waiting}</span>
          <span className={classes.statL}>Aguardando</span>
        </div>
        <div className={classes.stat}>
          <span className={classes.statV}>{api.uploadCounts.total}</span>
          <span className={classes.statL}>Total</span>
        </div>
        <div className={classes.countersRight}>
          {!api.isUploading && api.uploadCounts.error > 0 && (
            <Button
              onClick={api.retryFailed}
              color="white"
              size="sm"
              style={{ fontSize: 12, padding: "4px 12px", margin: 0 }}
            >
              <Refresh style={{ fontSize: 14, marginRight: 4 }} />
              Tentar novamente
            </Button>
          )}
        </div>
      </div>
      <div className={cx(classes.uploadList, classes.scroll)}>
        {api.uploads.map((u, idx) => (
          <UploadRow
            key={`${u.file.name}-${u.target}-${idx}`}
            classes={classes}
            row={u}
          />
        ))}
      </div>
    </>
  );
}

function UploadRow({ classes, row }) {
  const statusModifier = {
    waiting: classes.rowWaiting,
    uploading: classes.rowUploading,
    success: classes.rowSuccess,
    error: classes.rowError,
    interrupted: classes.rowInterrupted,
  }[row.status];
  // Glifos simples (sem circulo embutido) — o circulo de fundo vem do
  // ``.uploadIcon`` colorido por status, mantendo todos os 5 estados
  // com o MESMO peso visual.
  const Icon = {
    waiting: Schedule,
    uploading: ArrowUpward,
    success: Check,
    error: CloseIcon,
    interrupted: Remove,
  }[row.status];
  const statusLabel = {
    waiting: "Aguardando",
    uploading: `${row.progress}%`,
    success: "100%",
    error: "Falha",
    interrupted: "Interrompido",
  }[row.status];

  return (
    <div
      className={cx(classes.uploadRow, statusModifier)}
      title={row.error || undefined}
    >
      <div className={classes.uploadInfo}>
        <InsertDriveFile style={{ fontSize: 14, opacity: 0.6 }} />
        <span className={classes.uploadFile} title={row.file.name}>
          {row.file.name}
        </span>
        <span className={classes.uploadTarget}>{row.target}</span>
      </div>
      <div className={classes.progressBar}>
        <div
          className={cx(classes.progressFill, {
            [classes.progressFillIndeterminate]:
              row.status === "uploading" && row.progress === 0,
          })}
          style={
            row.status === "uploading" && row.progress === 0
              ? undefined
              : { width: `${row.progress}%` }
          }
        />
      </div>
      <div className={classes.pct}>{statusLabel}</div>
      <div className={classes.uploadIcon}>
        <Icon style={{ fontSize: 12 }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// PropTypes
// ─────────────────────────────────────────────────────────────────────

MultiMonthUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appName: PropTypes.string.isRequired,
  // Passamos o ``input`` inteiro (mesmo objeto que ``InputDrawerV3``
  // recebe). Lemos display_name/input_name/meta_name/expected_file_types
  // /volatility daqui dentro pra evitar duplicacao no call site.
  input: PropTypes.object,
  availableMonths: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
    }),
  ).isRequired,
  anchorYear: PropTypes.number.isRequired,
  anchorMonth: PropTypes.number.isRequired,
  onUploaded: PropTypes.func,
};

Step1Body.propTypes = {
  classes: PropTypes.object.isRequired,
  api: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  isSingleMode: PropTypes.bool.isRequired,
  expectedFileTypes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  fileInputRef: PropTypes.object.isRequired,
  folderInputRef: PropTypes.object.isRequired,
  onPickFiles: PropTypes.func.isRequired,
  onPickFolder: PropTypes.func.isRequired,
  onFilesChosen: PropTypes.func.isRequired,
  monthSummary: PropTypes.object.isRequired,
  overlapKeys: PropTypes.array.isRequired,
};

SingleUploadBody.propTypes = {
  classes: PropTypes.object.isRequired,
  api: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

Step2Body.propTypes = {
  classes: PropTypes.object.isRequired,
  api: PropTypes.object.isRequired,
  dupeKeys: PropTypes.instanceOf(Set).isRequired,
  dupeCount: PropTypes.number.isRequired,
};

ConfidencePill.propTypes = {
  classes: PropTypes.object.isRequired,
  confidence: PropTypes.string.isRequired,
};

Step3Body.propTypes = {
  classes: PropTypes.object.isRequired,
  api: PropTypes.object.isRequired,
};

UploadRow.propTypes = {
  classes: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
};

export default MultiMonthUploadModal;
